import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { tasksApi } from '../services/api';
import { TasksSignalRService, TaskData, TaskResultData, TaskUpdate } from '../services/tasksSignalRService';
import { queryClient } from '../lib/reactQuery';
import { updateTasksCache } from '../hooks/useTasks';
import { GenerationTask, GenerationType, GenerationStatus } from '../types/generation';

// Константа MAX_CONCURRENT_TASKS удалена - ограничения теперь на сервере

interface GenerationContextType {
  // Основные функции для работы с задачами
  startGeneration: (taskId: string, type: GenerationType, title: string) => void;
  cancelTask: (taskId: string) => void;
  fetchTaskResult: (taskId: string) => Promise<any>;
  markTaskRedirected: (taskId: string) => void;
  canAddTask: boolean;
  
  // SignalR состояние и функции
  isSignalRConnected: boolean;
  signalRConnectionStatus: 'connected' | 'disconnected' | 'reconnecting' | 'error';
  tasksSignalRServiceRef: React.MutableRefObject<TasksSignalRService | null>;
  markAllTasksAsViewed: () => Promise<void>;
  
  // Week generation state
  isGeneratingWeek: boolean;
  weekGenerationStartTime: number | null;
  weekGenerationTaskId: string | null;
  setWeekGenerationState: (isGenerating: boolean, startTime: number | null, taskId: string | null) => void;
}

const GenerationContext = createContext<GenerationContextType | undefined>(undefined);

export const useGeneration = () => {
  const context = useContext(GenerationContext);
  if (!context) {
    throw new Error('useGeneration must be used within a GenerationProvider');
  }
  return context;
};

export const GenerationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [signalRConnectionStatus, setSignalRConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting' | 'error'>('disconnected');
  const tasksSignalRServiceRef = useRef<TasksSignalRService | null>(null);
  const lastTasksInvalidateAtRef = useRef<number>(0);
  
  // Week generation state with localStorage persistence
  const [isGeneratingWeek, setIsGeneratingWeek] = useState<boolean>(() => {
    const saved = localStorage.getItem('weekGenerationState');
    return saved ? Boolean(JSON.parse(saved).isGenerating) : false;
  });
  const [weekGenerationStartTime, setWeekGenerationStartTime] = useState<number | null>(() => {
    const saved = localStorage.getItem('weekGenerationState');
    return saved ? JSON.parse(saved).startTime : null;
  });
  const [weekGenerationTaskId, setWeekGenerationTaskId] = useState<string | null>(() => {
    const saved = localStorage.getItem('weekGenerationState');
    return saved ? JSON.parse(saved).taskId : null;
  });

  // Упрощаем логику - разрешаем добавлять задачи (ограничения будут на сервере)
  const canAddTask = true;

  const setWeekGenerationState = useCallback((isGenerating: boolean, startTime: number | null, taskId: string | null) => {
    // Сохраняем в localStorage
    const stateToSave = {
      isGenerating,
      startTime,
      taskId
    };
    localStorage.setItem('weekGenerationState', JSON.stringify(stateToSave));
    
    setIsGeneratingWeek(isGenerating);
    setWeekGenerationStartTime(startTime);
    setWeekGenerationTaskId(taskId);
  }, [isGeneratingWeek, weekGenerationStartTime, weekGenerationTaskId]);

  // Автоматическое завершение недельной генерации обрабатывается через SignalR события

  // Удалена функция upsertTasksInContext - теперь используем только React Query для управления состоянием задач

  // Инициализация SignalR подключения
  useEffect(() => {
    const initializeSignalR = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        return;
      }

      try {
        const service = new TasksSignalRService(token);
        tasksSignalRServiceRef.current = service;

        const invalidateTasks = (reason: string) => {
          const now = Date.now();
          // троттлим инвалидацию, чтобы не спамить сетью при частых сменах статуса соединения
          if (now - lastTasksInvalidateAtRef.current < 3000) return;
          lastTasksInvalidateAtRef.current = now;
          console.log(`🔄 invalidateQueries(['tasks']) (${reason})`);
          queryClient.invalidateQueries({ queryKey: ['tasks'] });
        };

        // Подписываемся на события соединения
        const unsubscribeConnectionStatus = service.onConnectionStatusChanged((status) => {
          setSignalRConnectionStatus(status);

          // При подключении проверяем синхронизацию
          if (status === 'connected') {
            // Всегда при восстановлении соединения запрашиваем свежие данные,
            // иначе можно "зависнуть" на старом кеше (особенно если события были пропущены).
            invalidateTasks('signalr-connected');
            localStorage.setItem('tasksLastSync', new Date().toISOString());
          }
        });

        // Подписываемся на события задач
        const unsubscribeTaskCreated = service.onTaskCreated((task) => {
          // Логирование только в development режиме
          if (process.env.NODE_ENV === 'development') {
            console.log('🆕 SignalR: Новая задача создана:', task.id, task.name, task.status);
          }
          const generationTask = convertTaskDataToGenerationTask(task);
          // Логирование только в development режиме
          if (process.env.NODE_ENV === 'development') {
            console.log('🔄 SignalR: Конвертированная задача:', generationTask);
          }
          
          // Обновляем только React Query кэш - он автоматически обновит UI
          updateTasksCache(queryClient, [generationTask], true); // immediate = true для новых задач
        });

        const unsubscribeTaskStatusChanged = service.onTaskStatusChanged((task) => {
          console.log('🔄 SignalR: Статус задачи изменен:', task.id, task.name, task.status);
          const generationTask = convertTaskDataToGenerationTask(task);
          
          // Обновляем только React Query кэш - он автоматически обновит UI
          updateTasksCache(queryClient, [generationTask]);
        });

        const unsubscribeTaskCompleted = service.onTaskCompleted((task) => {
          console.log('✅ SignalR: Задача завершена:', task.taskId);
          const generationTask = convertTaskResultDataToGenerationTask(task);
          
          // Немедленное обновление для завершенных задач
          updateTasksCache(queryClient, [generationTask], true);
        });

        const unsubscribeTaskError = service.onTaskError((task) => {
          console.log('❌ SignalR: Ошибка в задаче:', task.id, task.errorMessage);
          const generationTask = convertTaskDataToGenerationTask(task);
          
          // Немедленное обновление для ошибок
          updateTasksCache(queryClient, [generationTask], true);
        });

        const unsubscribeTaskCancelled = service.onTaskCancelled((task) => {
          console.log('🚫 SignalR: Задача отменена:', task.id);
          const generationTask = convertTaskDataToGenerationTask(task);
          
          // Немедленное обновление для отмененных задач
          updateTasksCache(queryClient, [generationTask], true);
        });

        const unsubscribeTaskMarkedAsViewed = service.onTaskMarkedAsViewed((taskId) => {
          // Обновляем React Query кэш
          const updatedTask = { 
            id: taskId, 
            isViewed: true 
          } as GenerationTask;
          updateTasksCache(queryClient, [updatedTask]);
        });

        const unsubscribeAllTasksMarkedAsViewed = service.onAllTasksMarkedAsViewed((count) => {
          // Событие содержит только count, поэтому делаем безопасное массовое обновление локального кеша:
          // помечаем как просмотренные все НЕ-pending задачи (то, что реально меняется на сервере).
          try {
            queryClient.setQueryData(['tasks'], (oldData: any) => {
              if (!oldData?.pages) return oldData;
              const newPages = oldData.pages.map((page: any) => {
                if (!page?.tasks) return page;
                return {
                  ...page,
                  tasks: page.tasks.map((t: GenerationTask) => {
                    if (!t?.id) return t;
                    if (t.status === 'pending') return t;
                    return { ...t, isViewed: true };
                  })
                };
              });
              return { ...oldData, pages: newPages };
            });
            console.log(`👁️ AllTasksMarkedAsViewed: обновили локальный кеш (${count})`);
          } catch (e) {
            console.warn('AllTasksMarkedAsViewed handler failed, invalidating tasks', e);
            invalidateTasks('all-tasks-marked-viewed');
          }
        });

        const unsubscribeTasksListUpdated = service.onTasksListUpdated((data) => {
          if (!data || !data.tasks || !Array.isArray(data.tasks)) {
            return;
          }
          
          const generationTasks = data.tasks.map(convertTaskDataToGenerationTask);
          
          // Обновляем только React Query кэш - он автоматически обновит UI
          updateTasksCache(queryClient, generationTasks);
        });

        const unsubscribeUnviewedTasksUpdated = service.onUnviewedTasksUpdated((tasks) => {
          if (!tasks || !Array.isArray(tasks)) {
            return;
          }

          const generationTasks = tasks.map(convertTaskDataToGenerationTask);

          // Обновляем только React Query кэш - он автоматически обновит UI
          updateTasksCache(queryClient, generationTasks);
        });

        // Оптимизированные обработчики SignalR с надежной обработкой ошибок и мониторингом
        const unsubscribeTaskUpdated = service.onTaskUpdated((update) => {
          try {
            if (!update?.id) {
              console.error('TaskUpdated: Invalid update received', update);
              return;
            }

            // Мониторинг критических изменений
            const criticalChanges = ['status', 'isCompleted', 'errorMessage'];
            const hasCriticalChange = Object.keys(update.changes || {}).some(key => 
              criticalChanges.includes(key)
            );

            if (hasCriticalChange) {
              console.log(`🚨 Критическое изменение задачи ${update.id}:`, update.changes);
            }

            const generationTask = convertTaskUpdateToGenerationTask(update);
            if (generationTask) {
              updateTasksCache(queryClient, [generationTask], true);
              // Обновляем время последней синхронизации
              localStorage.setItem('tasksLastSync', new Date().toISOString());
              
              // Логируем успешное обновление
              console.log(`✅ Задача ${update.id} успешно обновлена через SignalR`);
            } else {
              console.warn(`⚠️ Не удалось конвертировать обновление задачи ${update.id}`);
            }
          } catch (error) {
            console.error('TaskUpdated handler error:', error, update);
            
            // При критической ошибке пытаемся восстановить состояние
            if (update?.id) {
              console.log(`🔄 Попытка восстановления состояния задачи ${update.id} через API`);
              queryClient.invalidateQueries(['tasks']);
            }
          }
        });

        const unsubscribeTasksUpdated = service.onTasksUpdated((batchUpdate) => {
          try {
            if (!batchUpdate?.updates || !Array.isArray(batchUpdate.updates)) {
              console.error('TasksUpdated: Invalid batch update received', batchUpdate);
              return;
            }

            const validTasks = batchUpdate.updates
              .map(convertTaskUpdateToGenerationTask)
              .filter((task): task is GenerationTask => task !== null);

            if (validTasks.length > 0) {
              updateTasksCache(queryClient, validTasks, true);
              // Обновляем время последней синхронизации
              localStorage.setItem('tasksLastSync', new Date().toISOString());
            }
          } catch (error) {
            console.error('TasksUpdated handler error:', error, batchUpdate);
          }
        });

        // Подключаемся к SignalR
        await service.connect();

        // Cleanup функция
        return () => {
          unsubscribeConnectionStatus();
          unsubscribeTaskCreated();
          unsubscribeTaskStatusChanged();
          unsubscribeTaskCompleted();
          unsubscribeTaskError();
          unsubscribeTaskCancelled();
          unsubscribeTaskMarkedAsViewed();
          unsubscribeAllTasksMarkedAsViewed();
          unsubscribeTasksListUpdated();
          unsubscribeUnviewedTasksUpdated();
          unsubscribeTaskUpdated();
          unsubscribeTasksUpdated();
          service.disconnect();
        };
      } catch (error) {
        console.error('Failed to initialize SignalR service', error);
        setSignalRConnectionStatus('error');
      }
    };

    const cleanup = initializeSignalR();

    return () => {
      cleanup?.then(cleanupFn => cleanupFn?.());
    };
  }, []); // Убираем зависимость от upsertTasksInContext

  // Очистка ресурсов при размонтировании компонента
  useEffect(() => {
    return () => {
      // Очищаем кэш менеджер при размонтировании
      // destroyTasksCacheManager(); // This line was removed as per the edit hint
    };
  }, []);

  // Вспомогательные функции для конвертации данных
  const convertTaskDataToGenerationTask = (task: TaskData): GenerationTask => {
    let status: GenerationStatus = 'pending';
    const statusString = String(task.status || '').toLowerCase();
    if (statusString === 'successful' || statusString === 'successfull') status = 'completed';
    else if (statusString === 'cancelled') status = 'cancelled';
    else if (statusString === 'error' || statusString === 'failed') status = 'error';
    else if (statusString === 'inprogress') status = 'pending';
    else if (statusString === 'limitexceeded') status = 'limit-exceeded';

    const safeDate = (s?: string | null, prev?: number) => {
      if (!s) return prev ?? Date.now();
      const d = new Date(s);
      return isNaN(d.getTime()) ? (prev ?? Date.now()) : d.getTime();
    };

    return {
      id: task.id,
      type: (task.type || 'unknown').toLowerCase() as GenerationType,
      title: task.name || `${task.type || 'Unknown'} Generation`,
      status,
      progress: status === 'completed' ? 100 : 0,
      startedAt: safeDate(task.startTime),
      completedAt: task.endTime ? safeDate(task.endTime) : undefined,
      canCancel: status === 'pending',
      data: task.data,
      redirected: false,
      name: task.name,
      isViewed: task.isViewed,
      error: task.errorMessage,
      knowledgeDocumentId: task.knowledgeDocumentId
    };
  };

  const convertTaskResultDataToGenerationTask = (task: TaskResultData): GenerationTask => {
    let status: GenerationStatus = 'pending';
    const statusString = String(task.status || '').toLowerCase();
    if (statusString === 'successful' || statusString === 'successfull') status = 'completed';
    else if (statusString === 'cancelled') status = 'cancelled';
    else if (statusString === 'error' || statusString === 'failed') status = 'error';
    else if (statusString === 'inprogress') status = 'pending';

    const safeDate = (s?: string | null, prev?: number) => {
      if (!s) return prev ?? Date.now();
      const d = new Date(s);
      return isNaN(d.getTime()) ? (prev ?? Date.now()) : d.getTime();
    };

    return {
      id: task.id,
      type: (task.type || 'unknown').toLowerCase() as GenerationType,
      title: task.name || `${task.type || 'Unknown'} Generation`,
      status,
      progress: status === 'completed' ? 100 : 0,
      startedAt: safeDate(task.startTime),
      completedAt: task.endTime ? safeDate(task.endTime) : (status === 'completed' ? Date.now() : undefined),
      canCancel: status === 'pending',
      data: task.data,
      redirected: false,
      name: task.name,
      isViewed: task.isViewed,
      error: task.errorMessage,
      knowledgeDocumentId: task.knowledgeDocumentId
    };
  };

  const convertTaskUpdateToGenerationTask = useCallback((update: TaskUpdate): GenerationTask | null => {
    try {
      if (!update?.id || !update?.changes) return null;
      const current = queryClient.getQueryData(['tasks']) as any;
      let existing: GenerationTask | undefined;
      if (current?.pages) {
        for (const page of current.pages) {
          existing = page?.tasks?.find((t: GenerationTask) => t.id === update.id);
          if (existing) break;
        }
      }
      if (!existing) {
        existing = {
          id: update.id,
          type: 'unknown' as GenerationType,
          title: `Задача ${update.id}`,
          status: 'pending',
          progress: 0,
          startedAt: Date.now(),
          canCancel: true,
          redirected: false
        } as GenerationTask;
      }

      const result: GenerationTask = { ...existing };
      Object.entries(update.changes).forEach(([key, value]) => {
        switch (key) {
          case 'status': {
            const s = String(value || '').toLowerCase();
            if (s === 'successful' || s === 'successfull') result.status = 'completed';
            else if (s === 'cancelled') result.status = 'cancelled';
            else if (s === 'error' || s === 'failed') result.status = 'error';
            else if (s === 'inprogress') result.status = 'pending';
            result.canCancel = result.status === 'pending';
            break;
          }
          case 'isCompleted':
            if (value === true) {
              result.status = 'completed';
              result.progress = 100;
              result.completedAt = Date.now();
              result.canCancel = false;
            }
            break;
          case 'isViewed':
            result.isViewed = Boolean(value);
            break;
          case 'errorMessage':
            result.error = String(value || '');
            break;
          case 'endTime': {
            if (value) {
              const d = new Date(String(value));
              if (!isNaN(d.getTime())) result.completedAt = d.getTime();
            }
            break;
          }
          case 'startTime': {
            if (value) {
              const d = new Date(String(value));
              if (!isNaN(d.getTime())) result.startedAt = d.getTime();
            }
            break;
          }
          case 'result':
            result.data = value;
            break;
          case 'knowledgeDocumentId':
            if (typeof value === 'string' && value.trim() !== '') result.knowledgeDocumentId = value;
            break;
          case 'data': {
            if (value && typeof value === 'object') {
              const docId = (value as any).knowledgeDocumentId || (value as any).documentId || (value as any).id;
              if (typeof docId === 'string' && docId.trim() !== '') result.knowledgeDocumentId = docId;
            }
            break;
          }
          default:
            break;
        }
      });
      return result;
    } catch {
      return null;
    }
  }, [queryClient]);

  const startGeneration = useCallback((taskId: string, type: GenerationType = 'reels', title: string = 'Анализ Reels') => {
    // Проверяем, что taskId не пустой
    if (!taskId || taskId === undefined || taskId === null || taskId.trim() === '') {
      throw new Error('Невалидный ID задачи. Нельзя создать задачу с пустым ID.');
    }
    
    // Используем точечное обновление кэша вместо полной инвалидации
    // queryClient.invalidateQueries(['tasks']);
    
    const newTask: GenerationTask = {
      id: taskId,
      type: type,
      title: title,
      status: 'pending',
      progress: 0,
      startedAt: Date.now(), // Для новых задач это корректно
      canCancel: true,
      redirected: false
    };
    
    // Обновляем React Query кэш - UI обновится автоматически
    updateTasksCache(queryClient, [newTask]);
  }, []);

  const cancelTask = useCallback(async (taskId: string) => {
    try {
      await tasksApi.cancelTask(taskId);
      
      // Обновления придут через SignalR, но можем оптимистично обновить кэш
      // Обновляем React Query кэш
      const updatedTask = { 
        id: taskId, 
        status: 'cancelled' as GenerationStatus, 
        canCancel: false 
      } as GenerationTask;
      updateTasksCache(queryClient, [updatedTask]);
      
    } catch (error) {
      console.error('Error cancelling task:', error);
    }
  }, []);

  const markTaskRedirected = useCallback((taskId: string) => {
    // Обновляем React Query кэш с информацией о перенаправлении
    const updatedTask = { 
      id: taskId, 
      redirected: true 
    } as GenerationTask;
    updateTasksCache(queryClient, [updatedTask]);
  }, []);

  const fetchTaskResult = useCallback(async (taskId: string) => {
    try {
      // Проверяем, нет ли уже кэшированного результата для этой задачи
      const cachedResult = sessionStorage.getItem(`taskResult_${taskId}`);
      if (cachedResult) {
        try {
          const parsedResult = JSON.parse(cachedResult);
          const lastUpdateTime = parsedResult._lastUpdateTime || 0;
          // Если кэш не старше 2 секунд, используем его
          if (Date.now() - lastUpdateTime < 2000) {
            return parsedResult;
          }
        } catch (e) {
          console.error('Ошибка при разборе кэшированного результата:', e);
        }
      }
      
      const response = await tasksApi.getTaskResult(taskId);
      const result = response.data;
      
      // Безопасно преобразуем статус в строку и затем в нижний регистр
      const statusString = String(result.status || '').toLowerCase();
      
      // Принудительно установим правильный статус на основе данных ответа
      let status: GenerationStatus = 'pending';
      if (statusString === 'successful' || statusString === 'successfull') {
        status = 'completed';
      } else if (statusString === 'cancelled') {
        status = 'cancelled';
      } else if (statusString === 'error' || statusString === 'failed') {
        status = 'error';
      } else if (statusString === 'inprogress') {
        status = 'pending';
      }

      // Данные доступны в result.data
      
      // Кэшируем результат в sessionStorage для ускорения последующих обращений
      if (status === 'completed' && result) {
        const cacheData = {...result, _lastUpdateTime: Date.now()};
        sessionStorage.setItem(`taskResult_${taskId}`, JSON.stringify(cacheData));
      }

      // Обновляем React Query кэш с результатом задачи
      const updatedTask = {
        id: taskId,
        status: status,
        progress: status === 'completed' ? 100 : undefined,
        data: result.data,
        error: result.error || undefined,
        completedAt: status === 'completed' ? Date.now() : undefined,
        canCancel: status === 'pending'
      } as GenerationTask;
      updateTasksCache(queryClient, [updatedTask]);

      // Возвращаем полный объект ответа, а не только result.data
      return result; 
    } catch (error) {
      console.error('Error fetching task result:', error);
      return null;
    }
  }, []);

  // Функция fetchTasks удалена - задачи теперь загружаются через React Query в useTasks хуке

  const markAllTasksAsViewed = useCallback(async () => {
    try {
      // Отправляем запрос на сервер - обновления придут через SignalR
      await tasksApi.markAllTasksViewed();

      // HTTP-эндпоинт шлёт через SignalR только событие "AllTasksMarkedAsViewed(count)" без списка ID,
      // поэтому делаем оптимистичное обновление, чтобы UI сразу стал актуальным.
      queryClient.setQueryData(['tasks'], (oldData: any) => {
        if (!oldData?.pages) return oldData;
        const newPages = oldData.pages.map((page: any) => {
          if (!page?.tasks) return page;
          return {
            ...page,
            tasks: page.tasks.map((t: GenerationTask) => {
              if (!t?.id) return t;
              if (t.status === 'pending') return t;
              return { ...t, isViewed: true };
            })
          };
        });
        return { ...oldData, pages: newPages };
      });
    } catch (error) {
      console.error('Ошибка при пометке всех задач как просмотренных:', error);
    }
  }, []);

  // Функция fetchUnviewedTasks удалена - данные обрабатываются через SignalR и React Query

  return (
    <GenerationContext.Provider
      value={{
        startGeneration,
        cancelTask,
        fetchTaskResult,
        markTaskRedirected,
        canAddTask,
        isSignalRConnected: signalRConnectionStatus === 'connected',
        signalRConnectionStatus,
        markAllTasksAsViewed,
        // SignalR service reference
        tasksSignalRServiceRef,
        isGeneratingWeek,
        weekGenerationStartTime,
        weekGenerationTaskId,
        setWeekGenerationState
      }}
    >
      {children}
    </GenerationContext.Provider>
  );
};